'use client';

import { ArrowForward, LoginRounded } from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  ButtonGroup,
  Container,
  Modal,
  Toolbar,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BlocksContent } from '@strapi/blocks-react-renderer';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DesktopMegaMenu } from '@/components/Nav/DesktopMegaMenu';
import { MobileMegaMenu } from '@/components/Nav/MobileMegaMenu';
import { NestedMenu } from '@/lib/queries/Menus';
import { normalizeLinkHref } from '@/lib/UrlHelpers';
import { CustomShadows } from '@/theme';
import { ComponentSharedSiteBanner } from '@/types';

import { FormGetStarted } from '../Forms/FormGetStarted';
import { FormLendIQSignIn } from '../Forms/FormLendIQSignIn';
import Banners from './Banners';

const HOMEPAGE_TRANSPARENT_HEADER_SCROLL_Y = 120;
const BROKER_LOGIN_PAGE_PATH = '/broker';

export default function NavBar({
  banners,
  getStartedUrl,
  signInUrl,
  contactUsPrompt,
  contactUsNumber,
  contactUsFormHeader,
  menus,
  headerTheme = 'default',
  showLogin = false,
}: {
  banners: ComponentSharedSiteBanner[];
  getStartedUrl: string;
  signInUrl: string;
  contactUsNumber: string;
  contactUsPrompt: string;
  contactUsFormHeader: BlocksContent;
  menus: NestedMenu[];
  headerTheme?: 'default' | 'dark';
  showLogin?: boolean;
}) {
  const theme = useTheme();
  const pathname = usePathname();

  const isDarkTheme = headerTheme === 'dark';
  const shouldShowIQLogin = showLogin;
  const loginRef = useRef<HTMLDivElement>(null);

  const isMarketingPage = [
    '/',
    BROKER_LOGIN_PAGE_PATH,
    '/about',
    '/sitemap',
    '/privacy',
    '/terms',
    '/terms-of-use',
    '/lend-partner-service-terms',
  ].includes(pathname);
  const isHomepage = pathname === '/';
  const isBrokerLoginPage = pathname === BROKER_LOGIN_PAGE_PATH;

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!isHomepage) return;
    const onScroll = () => setScrolled(window.scrollY > HOMEPAGE_TRANSPARENT_HEADER_SCROLL_Y);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHomepage]);
  const isHomepageAtTop = isHomepage && !scrolled;

  const [formOpen, setOpenForm] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginAnchorEl, setLoginAnchorEl] = useState<HTMLElement | null>(null);

  const handleFormOpen = useCallback(() => setOpenForm(true), []);
  const handleFormClose = useCallback(() => setOpenForm(false), []);
  const handleLoginOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setLoginAnchorEl(event.currentTarget);
    setLoginOpen(true);
  }, []);
  const handleLoginClose = useCallback(() => {
    setLoginOpen(false);
    setLoginAnchorEl(null);
  }, []);

  const handleToggleLogin = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (loginOpen) handleLoginClose();
      else handleLoginOpen(event);
    },
    [loginOpen, handleLoginClose, handleLoginOpen]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (loginRef.current && !loginRef.current.contains(event.target as Node)) {
        handleLoginClose();
      }
    };

    if (loginOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [loginOpen, handleLoginClose]);

  const mainMenu = menus.find((menu) => menu.MenuKey === 'main');
  const hasMainMenu = mainMenu && mainMenu.MenuItems && mainMenu.MenuItems.length > 0;

  const talkToUs = useMemo(
    () =>
      isMarketingPage && (
        <Button
          variant="outlined"
          className="talk-to-us-button"
          sx={{
            color: theme.palette.brand.primary,
            borderColor: theme.palette.purple?.[500] ?? theme.palette.primary.main,
            whiteSpace: 'nowrap',
            [theme.breakpoints.up('lg')]: {
              color: theme.palette.common.white,
              background: theme.palette.brand.brandDark,
              border: 'none',
              borderLeft: `1px solid ${alpha(theme.palette.common.white, 0.5)}`,
              fontSize: theme.typography.pxToRem(13),
              fontWeight: 'normal',
              borderRadius: 0,
              borderBottomRightRadius: 2,
              py: theme.spacing(0.75),
            },
            ':hover': {
              backgroundColor: alpha(theme.palette.brand.primary, 0.1),
              borderColor: theme.palette.brand.primary,
              [theme.breakpoints.up('lg')]: {
                backgroundColor: theme.palette.brand.primary,
              },
            },
          }}
          onClick={handleFormOpen}
        >
          Talk to Us
        </Button>
      ),
    [theme, handleFormOpen, isMarketingPage]
  );

  const marketingSignupLabel = isBrokerLoginPage ? 'Broker Sign Up' : 'Partner Sign up';
  const marketingSignInLabel = isHomepage
    ? 'Broker Sign in'
    : isBrokerLoginPage
      ? 'Broker Login'
      : 'Partner Sign in';
  const brokerPageLinkLabel = isHomepage ? 'For Brokers' : 'Broker Login';
  const mobileHeaderActionHref = isHomepage ? BROKER_LOGIN_PAGE_PATH : normalizeLinkHref(signInUrl);
  const mobileHeaderActionLabel = isHomepage ? brokerPageLinkLabel : marketingSignInLabel;

  const { gettingStartedButton, gettingStartedLink } = useMemo(() => {
    const compareNowUrl = process.env.NEXT_PUBLIC_FULL_APP_URL;
    const href = normalizeLinkHref(
      isHomepage ? BROKER_LOGIN_PAGE_PATH : isMarketingPage ? getStartedUrl : (compareNowUrl ?? '#')
    );
    const label = isHomepage
      ? brokerPageLinkLabel
      : isMarketingPage
        ? marketingSignupLabel
        : 'Compare Now';

    return {
      gettingStartedButton: (
        <Button
          href={href}
          variant="contained"
          color="primary"
          endIcon={isMarketingPage && !isHomepage ? <ArrowForward /> : undefined}
          className="getting-started-button"
          sx={{
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Button>
      ),
      gettingStartedLink: (
        <Button
          href={href}
          size="small"
          variant="contained"
          className="getting-started-link"
          endIcon={isMarketingPage && !isHomepage ? <ArrowForward /> : undefined}
        >
          {label}
        </Button>
      ),
    };
  }, [brokerPageLinkLabel, getStartedUrl, isHomepage, isMarketingPage, marketingSignupLabel]);

  const mobileMenuGettingStartedButton = isHomepage ? (
    <Button
      href={normalizeLinkHref(signInUrl)}
      variant="contained"
      color="primary"
      className="getting-started-button"
      sx={{
        whiteSpace: 'nowrap',
      }}
    >
      Broker Sign In
    </Button>
  ) : (
    gettingStartedButton
  );

  const mobileHeaderLinkSx = {
    color: theme.palette.brand.brandDark,
    textDecoration: 'underline',
    display: {
      xs: 'block',
      lg: 'none',
    },
    px: { xs: 0.5, sm: 1 },
    fontSize: { xs: theme.typography.pxToRem(13), sm: theme.typography.pxToRem(14) },
    '@media (max-width:359.95px)': {
      display: 'none',
    },
    '&:hover,&:focus': {
      color: theme.palette.brand.brand,
    },
  };

  return (
    <>
      <AppBar
        data-component="NavBar"
        position="fixed"
        sx={{
          ...(isDarkTheme && {
            backgroundColor: theme.palette.purple[800],
            color: theme.palette.common.white,
            backgroundImage: 'none',
          }),
          ...(isHomepageAtTop &&
            !isDarkTheme && {
              backgroundColor: { xs: theme.palette.common.white, lg: 'transparent' },
              backgroundImage: 'none',
              borderBottom: {
                xs: `1px solid ${alpha(theme.palette.brand.bodyText, 0.08)}`,
                lg: 'none',
              },
            }),
          boxShadow: 'none',
        }}
      >
        <Banners banners={banners} />

        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: '100%',
            px: { xs: 1.5, sm: 3, md: 4 },
            py: { xs: 0.25, sm: 0.75, md: 1 },
            mx: 'auto',
            transition: 'padding 0.2s ease',
          }}
        >
          <Box sx={{ maxWidth: 2000, mx: 'auto' }}>
            <Toolbar
              disableGutters
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: { xs: 52, sm: 60, md: 68 },
                pl: { xs: 0, sm: 1 },
                pr: 0,
                mt: 0.95,
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  '.logo-link--desktop': {
                    display: { xs: 'none', sm: 'block' },
                    mr: 2,
                  },
                  '.logo-link--mobile': {
                    display: { xs: 'block', sm: 'none' },
                    mr: 1.5,
                  },
                }}
              >
                <Link href="/" className="logo-link--desktop">
                  <Image
                    src={isDarkTheme ? '/assets/logo-white.svg' : '/assets/logo-color.svg'}
                    alt="Lend Logo"
                    width={150} // ⬇️ slightly smaller
                    height={42}
                  />
                </Link>
                <Link href="/" className="logo-link--mobile">
                  <Image
                    src={isDarkTheme ? '/assets/logo-white.svg' : '/assets/logo-color.svg'}
                    alt="Lend Logo"
                    width={110}
                    height={22}
                  />
                </Link>
              </Box>

              {/* Menus */}
              {hasMainMenu && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flexGrow: 1,
                    flexShrink: 1,
                    gap: { xs: 0.5, sm: 1.5, md: 2 },
                    minWidth: 0,
                    overflow: 'visible',
                  }}
                >
                  {mainMenu && (
                    <DesktopMegaMenu
                      menu={mainMenu}
                      variant={isDarkTheme ? 'dark' : 'light'}
                      maxLevelAdjust={shouldShowIQLogin ? -1 : 0}
                      maxLevelAdjustXL={shouldShowIQLogin ? -2 : 0}
                    />
                  )}
                </Box>
              )}

              {/* Right actions */}
              <Box
                className="nav-actions"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  flexShrink: 0,
                }}
              >
                {shouldShowIQLogin && (
                  <Box ref={loginRef} sx={{ position: 'relative' }}>
                    <Button
                      onClick={handleToggleLogin}
                      variant="text"
                      endIcon={<LoginRounded />}
                      sx={{
                        mt: `calc(${theme.spacing(0.3)} * -1)`,
                        ml: 4,
                        borderRadius: 20,
                        color: theme.palette.common.white,
                        background: alpha(theme.palette.brand.primary, 0.15),
                        transition: 'all 0.2s ease',
                        ':hover,:focus': {
                          background: theme.palette.gradients.gradient1,
                          boxShadow: (theme.shadows as CustomShadows).a,
                        },
                      }}
                    >
                      Lend IQ
                    </Button>

                    {loginOpen && (
                      <FormLendIQSignIn
                        open={loginOpen}
                        onClose={handleLoginClose}
                        anchorEl={loginAnchorEl}
                      />
                    )}
                  </Box>
                )}
                {!shouldShowIQLogin && isMarketingPage && (
                  <ButtonGroup
                    sx={{
                      display: {
                        xs: 'none',
                        lg: 'inline-flex',
                      },
                      position: 'absolute',
                      top: `calc(${theme.spacing(4)} * -1)`,
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      overflow: 'hidden',
                      right: 0,
                      a: {
                        borderRadius: `0 !important`,
                        fontWeight: 'normal',
                        px: 2,
                        py: 0.5,
                        '&:hover,&:focus': {
                          textDecoration: 'underline',
                        },
                        '&.MuiButton-containedSecondary, &.talk-to-us-button': {
                          borderRadius: 0,
                          background: theme.palette.brand.brandDark,
                          color: theme.palette.common.white,
                          '&:hover,&:focus': {
                            backgroundColor: theme.palette.brand.primary,
                            textDecoration: 'underline',
                          },
                        },
                      },
                    }}
                  >
                    {!isHomepage && gettingStartedLink}
                    {isHomepage && (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        endIcon={<ArrowForward />}
                        href={BROKER_LOGIN_PAGE_PATH}
                      >
                        {brokerPageLinkLabel}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      color="secondary"
                      href={normalizeLinkHref(signInUrl)}
                    >
                      {marketingSignInLabel}
                    </Button>
                    {talkToUs}
                  </ButtonGroup>
                )}
                {!shouldShowIQLogin && isMarketingPage && (
                  <Button variant="text" href={mobileHeaderActionHref} sx={mobileHeaderLinkSx}>
                    {mobileHeaderActionLabel}
                  </Button>
                )}
                {hasMainMenu && (
                  <MobileMegaMenu
                    menu={mainMenu || undefined}
                    variant={isDarkTheme ? 'dark' : 'light'}
                    {...{
                      gettingStarted: mobileMenuGettingStartedButton,
                      ...(isMarketingPage ? { talkToUs } : {}),
                    }}
                  />
                )}
              </Box>
            </Toolbar>
          </Box>
        </Container>
      </AppBar>

      <Modal open={formOpen} onClose={handleFormClose} aria-labelledby="contact-form-modal">
        <FormGetStarted
          handleClose={handleFormClose}
          content={
            contactUsNumber || contactUsPrompt || contactUsFormHeader
              ? {
                  contactUsNumber,
                  contactUsPrompt,
                  contactUsFormHeader,
                }
              : undefined
          }
        />
      </Modal>
    </>
  );
}
